/**
 * Cabeçalho fixo do website (vidro ao rolar, menu mobile)
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ArrowRight, LayoutDashboard, ChevronDown } from 'lucide-react';
import { logoHorizontalDark, logoHorizontalLight } from '@assets/index';
import { SiteThemeToggle } from './SiteThemeToggle';
import { lockBodyScroll } from '@/utils/scrollLock';
import { BRAND } from '../content';
import { SEGMENT_PAGES } from '../segments';

const NAV = [
  { href: '#funcionalidades', label: 'Funcionalidades' },
  { href: '#economia', label: 'Economia' },
  { href: '#implantacao', label: 'Implantação' },
  { href: '#planos', label: 'Planos' },
  { href: '#faq', label: 'Dúvidas' },
];

interface SiteHeaderProps {
  isAuthenticated: boolean;
  /** Prefixo das âncoras: "" na home, "/" nas páginas de segmento */
  basePath?: string;
  /** Link do botão de teste grátis (ex.: já com o segmento) */
  signupHref?: string;
}

/** Menu "Para o seu negócio" (desktop) */
function SegmentsMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <li ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls="vw-segments-menu"
        className="flex items-center gap-1 whitespace-nowrap rounded-lg px-2.5 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-white 2xl:px-3"
      >
        Para o seu negócio
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          id="vw-segments-menu"
          className="absolute left-0 top-full mt-2 w-80 rounded-2xl border border-white/10 bg-[#0b0f1c] p-2 shadow-2xl shadow-black/60"
        >
          {SEGMENT_PAGES.map(s => (
            <Link
              key={s.slug}
              to={`/para/${s.slug}`}
              onClick={() => setOpen(false)}
              className="flex items-start gap-3 rounded-xl p-3 transition hover:bg-white/5"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-500/15 text-violet-200">
                <s.icon size={18} aria-hidden />
              </span>
              <span>
                <span className="block text-sm font-semibold text-white">{s.label}</span>
                <span className="mt-0.5 block text-xs leading-snug text-slate-400">
                  {s.highlights.join(' · ')}
                </span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </li>
  );
}

export function SiteHeader({
  isAuthenticated,
  basePath = '',
  signupHref = '/cadastro',
}: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  /** Devolver o foco ao botão do menu quando ele fechar (Esc ou o próprio botão) */
  const returnFocusRef = useRef(false);

  const closeMenu = (returnFocus: boolean) => {
    returnFocusRef.current = returnFocus;
    setOpen(false);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Menu mobile aberto: trava o scroll do body, foca o primeiro item e fecha com Esc
  useEffect(() => {
    if (!open) {
      if (returnFocusRef.current) {
        returnFocusRef.current = false;
        menuButtonRef.current?.focus();
      }
      return;
    }
    const unlock = lockBodyScroll();
    mobileMenuRef.current?.querySelector<HTMLElement>('a[href], button:not([disabled])')?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      unlock();
    };
  }, [open]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? 'border-b border-white/10 bg-[#070a13]/80 backdrop-blur-xl'
          : 'bg-transparent'
      }`}
    >
      <nav
        className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:h-20 lg:px-8"
        aria-label="Principal"
      >
        <a
          href={basePath ? '/' : '#inicio'}
          className="flex shrink-0 items-center"
          aria-label={`${BRAND} - início`}
        >
          {/* Logo para fundo escuro e para fundo claro (o CSS mostra a do tema atual) */}
          <img src={logoHorizontalDark} alt={BRAND} className="vw-logo-on-dark h-6 w-auto sm:h-7" />
          <img src={logoHorizontalLight} alt={BRAND} className="vw-logo-on-light h-6 w-auto sm:h-7" />
        </a>

        <ul className="hidden items-center gap-1 xl:flex">
          <SegmentsMenu />
          {NAV.map(item => (
            <li key={item.href}>
              <a
                href={`${basePath}${item.href}`}
                className="whitespace-nowrap rounded-lg px-2.5 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-white 2xl:px-3"
              >
                {item.label}
              </a>
            </li>
          ))}
          <li>
            <Link
              to="/blog"
              className="whitespace-nowrap rounded-lg px-2.5 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-white 2xl:px-3"
            >
              Blog
            </Link>
          </li>
        </ul>

        <div className="hidden items-center gap-2 xl:flex 2xl:gap-3">
          <SiteThemeToggle />
          {isAuthenticated ? (
            <Link
              to="/painel"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-violet-100"
            >
              <LayoutDashboard size={16} /> Acessar meu painel
            </Link>
          ) : (
            <>
              <Link
                to="/entrar"
                className="whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold text-slate-200 hover:text-white"
              >
                Entrar
              </Link>
              <Link
                to={signupHref}
                className="group inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-900/40 transition hover:brightness-110"
              >
                Teste grátis 30 dias
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </Link>
            </>
          )}
        </div>

        <SiteThemeToggle className="ml-auto mr-2 xl:hidden" />
        <button
          ref={menuButtonRef}
          type="button"
          onClick={() => (open ? closeMenu(true) : setOpen(true))}
          className="rounded-lg p-2 text-slate-200 hover:bg-white/10 xl:hidden"
          aria-expanded={open}
          aria-controls="vw-mobile-menu"
          aria-label={open ? 'Fechar menu' : 'Abrir menu'}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div
          id="vw-mobile-menu"
          ref={mobileMenuRef}
          className="h-[calc(100dvh-4rem)] overflow-y-auto border-t border-white/10 px-4 pb-8 pt-4 xl:hidden"
        >
          <ul className="space-y-1">
            {NAV.map(item => (
              <li key={item.href}>
                <a
                  href={`${basePath}${item.href}`}
                  onClick={() => closeMenu(false)}
                  className="block rounded-xl px-4 py-3 text-base font-medium text-slate-200 hover:bg-white/5"
                >
                  {item.label}
                </a>
              </li>
            ))}
            <li>
              <Link
                to="/blog"
                onClick={() => closeMenu(false)}
                className="block rounded-xl px-4 py-3 text-base font-medium text-slate-200 hover:bg-white/5"
              >
                Blog
              </Link>
            </li>
          </ul>
          <p className="mt-6 px-4 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
            Para o seu negócio
          </p>
          <ul className="mt-2 grid grid-cols-2 gap-2">
            {SEGMENT_PAGES.map(s => (
              <li key={s.slug}>
                <Link
                  to={`/para/${s.slug}`}
                  onClick={() => closeMenu(false)}
                  className="flex h-full items-center gap-2 rounded-xl border border-white/10 px-3 py-3 text-sm font-medium text-slate-200 hover:bg-white/5"
                >
                  <s.icon size={16} className="shrink-0 text-violet-300" aria-hidden />
                  {s.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-6 grid gap-3">
            {isAuthenticated ? (
              <Link
                to="/painel"
                onClick={() => closeMenu(false)}
                className="rounded-xl bg-white px-4 py-3 text-center font-semibold text-slate-900"
              >
                Acessar meu painel
              </Link>
            ) : (
              <>
                <Link
                  to={signupHref}
                  onClick={() => closeMenu(false)}
                  className="rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-3 text-center font-semibold text-white"
                >
                  Começar teste grátis
                </Link>
                <Link
                  to="/entrar"
                  onClick={() => closeMenu(false)}
                  className="rounded-xl border border-white/15 px-4 py-3 text-center font-semibold text-white"
                >
                  Entrar
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
